import { Navigate, useParams, useSearchParams } from 'react-router-dom';

import * as diagnostics from '@/utils/diagnostics';

import { ROUTE_KEYS } from './constants';
import { getCompletePathFromRouteKey } from './navigationUtils';

enum RedirectPaths {
  PublicError = 'public-error',
  Proposal = 'proposal',
  Task = 'task',
  PrintPresentation = 'print-presentation',
  Memo = 'memo',
}

function getNormalizedRedirectPath(
  destination: RedirectPaths,
  params: URLSearchParams
): string {
  // TODO: Add additional validation that the query params are what we expect
  switch (destination) {
    case RedirectPaths.PublicError: {
      return getCompletePathFromRouteKey(ROUTE_KEYS.PUBLIC_ERROR, {
        errorCode: params.get('code') || 'UNKNOWN',
      });
    }
    case RedirectPaths.Proposal: {
      // This redirect is only for backwards compatibility with proposal share
      // links that contain a SHARE_PROPOSAL token. The app no longer creates
      // these links. Remove this redirect after 2027-01-13 (T2-11870).
      return getCompletePathFromRouteKey(
        ROUTE_KEYS.HOUSEHOLD_PROPOSAL_PRESENTATION,
        {
          proposalId: params.get('proposalId')!,
        }
      );
    }
    case RedirectPaths.Task: {
      return getCompletePathFromRouteKey(ROUTE_KEYS.TASK_STANDALONE, {
        taskId: params.get('taskId')!,
      });
    }
    case RedirectPaths.PrintPresentation: {
      return getCompletePathFromRouteKey(
        ROUTE_KEYS.HOUSEHOLD_DETAILS_DOCUMENTS,
        {
          householdId: params.get('householdId')!,
        },
        {
          // this is the document ID of the presentation that the user will download
          // upon redirection
          downloadDocumentId: params.get('documentId')!,
        }
      );
    }
    case RedirectPaths.Memo: {
      return getCompletePathFromRouteKey(
        ROUTE_KEYS.HOUSEHOLD_DETAILS_MEMO_DETAILS,
        {
          householdId: params.get('householdId')!,
          memoId: params.get('memoId')!,
        }
      );
    }
    default: {
      return getCompletePathFromRouteKey(ROUTE_KEYS.PUBLIC_ERROR, {
        errorCode: 'UNKNOWN',
      });
    }
  }
}

function getValidatedRedirectPath(destination?: string): RedirectPaths {
  const redirectPathValues = Object.values(RedirectPaths) as string[];
  if (!destination || !redirectPathValues.includes(destination)) {
    diagnostics.error(
      'Invalid redirect path',
      new Error(
        `No specified redirect path found for destination ${destination}; redirecting to ${RedirectPaths.PublicError}`
      )
    );
    return RedirectPaths.PublicError;
  }

  return destination as RedirectPaths;
}

export function FunctionalRedirect() {
  const { destination } = useParams();
  const [searchParams] = useSearchParams();

  const redirectPath = getNormalizedRedirectPath(
    getValidatedRedirectPath(destination),
    searchParams
  );
  return <Navigate to={redirectPath} replace />;
}
